import { z } from "zod";

// 5-digit ZIP or ZIP+4 (existing owner data is mostly 5-digit)
const ZIP_REGEX = /^[0-9]{5}(-[0-9]{4})?$/;

const optionalText = z.string().optional().nullable();

const addressSchema = z.object({
  addressLine1: optionalText,
  addressLine2: optionalText,
  pobox: optionalText,
  city: optionalText,
  state: optionalText,
  zipCode: optionalText,
  country: optionalText,
});

const requireAddress = (address: any, path: string, ctx: z.RefinementCtx) => {
  const required: [string, string][] = [
    ["addressLine1", "Please enter Address Line 1."],
    ["city", "Please enter City."],
    ["state", "Please select State."],
    ["zipCode", "Please enter ZIP Code."],
  ];
  required.forEach(([field, message]) => {
    if (!address?.[field]?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path, field] });
    }
  });
  if (address?.zipCode?.trim() && !ZIP_REGEX.test(address.zipCode.trim())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Enter ZIP code as XXXXX or XXXXX-XXXX.",
      path: [path, "zipCode"],
    });
  }
};

const ownerSchema = (emailRequired: boolean) => z
  .object({
    title: z.string().max(5, "Title can be at most 5 characters.").optional().nullable(),
    firstName: z.string().trim().min(2, "Please enter First Name.").max(30, "First Name can be at most 30 characters."),
    lastName: z
      .string()
      .trim()
      .max(30, "Last Name can be at most 30 characters.")
      .refine((v) => !v || v.length >= 2, "Last Name must be at least 2 characters.")
      .optional()
      .nullable(),
    email: emailRequired
      ? z.string().trim().min(1, "Please enter Primary Email.").email("This is not a valid email.")
      : z.string().trim().refine((v) => !v || z.string().email().safeParse(v).success, "This is not a valid email.")
          .optional().nullable(),
    email2: z
      .string()
      .trim()
      .refine((v) => !v || z.string().email().safeParse(v).success, "This is not a valid email.")
      .optional()
      .nullable(),
    phoneNumber: z.string().min(10, "Please enter Mobile Number."),
    landlineNumber: optionalText,
    address: addressSchema,
    separateCorrespondence: z.boolean(),
    correspondenceAddress: addressSchema,
  })
  .superRefine((data, ctx) => {
    requireAddress(data.address, "address", ctx);
    if (data.separateCorrespondence) {
      requireAddress(data.correspondenceAddress, "correspondenceAddress", ctx);
    }
  });

/** Edit Owner: primary email optional (owners may have none). */
export const schema = ownerSchema(false);
/** Add Owner (Owner Master + Add): primary email optional. */
export const createOwnerSchema = ownerSchema(false);
